import React from 'react'
import { Controller, useFormContext, useWatch } from 'react-hook-form'

import { pxToRem } from '@/shared/styles/themes'
import { Divider, FormControlLabel, FormHelperText, Grid2 as Grid, Switch, Typography } from '@mui/material'
import { DateTimePicker } from '@mui/x-date-pickers'

import { AddressFields, AddressValue } from '../AddressFields/AddressFields'
import { MapField } from '../AddressFields/MapField'
import { Checkbox } from '../Checkbox/Checkbox'
import { DatePicker } from '../DatePicker/DatePicker'
import { FileInputField } from '../FileInputField/FileInputField'
import { MultiselectDropdown } from '../MultiselectDropdown/MultiselectDropdown'
import { OpeningHoursField } from '../OpeningHoursField/OpeningHoursField'
import { OrderItemUpdate } from '../OrderItemsField/models/OrderItemUpdate'
import { OrderItemsField } from '../OrderItemsField/OrderItemsField'
import { RadioGroup } from '../RadioGroup/RadioGroup'
import { SelectDropdown } from '../SelectDropdown/SelectDropdown'
import { TextField } from '../TextField/TextField'
import { TimePicker } from '../TimePicker/TimePicker'
import { evaluateVisibilityCondition } from './libs/evaluateVisibilityCondition'
import { DynamicFieldData, SelectOption } from './models/DynamicFieldData'

export type DynamicFormFieldProps = DynamicFieldData & {
  //
}

export const DynamicFormField: React.FC<DynamicFormFieldProps> = ({ defaultValue: _defaultValue, grid, ...field }) => {
  const { register, control, setError } = useFormContext()

  // Watch dependencies
  const dependentFields = field?.dependencies || []
  const dependentFieldNames = [...new Set(dependentFields.map(dep => dep.fieldName))]

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const dependentDefaultValues = dependentFields.reduce<Record<string, any>>(((accumulator, dep) => {
    accumulator[dep.fieldName] = dep.defaultValue

    return accumulator
  }), {})
  const watchedValues = useWatch({
    control: control,
    name: dependentFieldNames,
    defaultValue: dependentDefaultValues,
  })
  const isFieldVisible = evaluateVisibilityCondition(field.dependencies, watchedValues, dependentFieldNames)

  // Field grid column properties
  const gridProps = grid || { xs: 12 }

  // Select form field component to render based on field type
  let fieldComponent = null

  if (field.fieldType === 'text' || field.fieldType === 'number' || field.fieldType === 'password' || field.fieldType === 'email') {
    const { fieldType, fieldName, label, placeholder, config, ...fieldProps } = field

    fieldComponent = <Controller
      {...register(fieldName, config)}
      control={control}
      render={({ field: controlField, fieldState }) => {
        return (
          <TextField
            {...fieldProps}
            {...controlField}
            label={label}
            type={fieldType}
            placeholder={placeholder}
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
            fullWidth
            margin="normal"
            autoComplete="off"
            inputProps={{
              form: {
                autoComplete: 'off', // Disable browser autocomplete for dynamic form fields
              },
            }}
          />
        )
      }}
    />
  } else if (field.fieldType === 'textarea') {
    const { fieldName, fieldType, label, placeholder, config, ...fieldProps } = field

    fieldComponent = <Controller
      {...register(fieldName, config)}
      control={control}
      render={({ field: controlField, fieldState }) => {
        return (
          <TextField
            {...fieldProps}
            {...controlField}
            label={label}
            rows={3}
            multiline
            placeholder={placeholder}
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
            fullWidth
            margin="normal"
          />
        )
      }}
    />
  } else if (field.fieldType === 'select') {
    const { fieldName, fieldType, label, options, placeholder, config, ...fieldProps } = field

    fieldComponent = <Controller
      {...register(fieldName, config)}
      control={control}
      render={({ field: controlField, fieldState }) => {
        return (
          <SelectDropdown
            {...fieldProps}
            {...controlField}
            label={label}
            value={controlField.value}
            onChange={(_, value) => {
              controlField.onChange(value)
            }}
            placeholder={placeholder}
            options={options}
            getOptionLabel={(option) => {
              return (option as SelectOption).label
            }}
            isOptionEqualToValue={(option, selectedOption) => {
              return (option as SelectOption).value === (selectedOption as SelectOption).value
            }}
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
          />
        )
      }}
    />
  } else if (field.fieldType === 'multiselect') {
    const { fieldName, fieldType, label, placeholder, options, config, ...fieldProps } = field

    fieldComponent = <Controller
      {...register(fieldName, config)}
      control={control}
      render={({ field: controlField, fieldState }) => {
        return (
          <MultiselectDropdown
            {...fieldProps}
            value={controlField.value}
            onChange={(_, value) => controlField.onChange(value)}
            ref={controlField.ref}
            label={label}
            placeholder={placeholder}
            options={options}
            getOptionLabel={(option) => {
              return (option as SelectOption).label
            }}
            isOptionEqualToValue={(option, selectedOption) => {
              return (option as SelectOption).value === (selectedOption as SelectOption).value
            }}
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
          />
        )
      }}
    />
  } else if (field.fieldType === 'date') {
    const { fieldName, fieldType, label, disablePast, placeholder, config, ...fieldProps } = field

    fieldComponent = <Controller
      {...register(fieldName, config)}
      control={control}
      render={({ field: controlField, fieldState }) => {
        return (
          <DatePicker
            {...controlField}
            {...fieldProps}
            label={label}
            format="DD/MM/YYYY"
            onAccept={controlField.onChange}
            disablePast={disablePast}
            slotProps={{
              textField: {
                fullWidth: true,
                placeholder: placeholder,
                error: !!fieldState?.error,
                helperText: fieldState?.error?.message,
              },
            }}
            sx={{ display: isFieldVisible ? 'block' : 'true' }}
          />
        )
      }}
    />
  } else if (field.fieldType === 'time') {
    const { fieldName, fieldType: _fieldType, label, views, format, disablePast, placeholder, config, ...fieldProps } = field

    fieldComponent = <Controller
      {...register(fieldName, config)}
      control={control}
      render={({ field: controlField, fieldState }) => {
        return (
          <TimePicker
            {...controlField}
            {...fieldProps}
            label={label}
            onAccept={controlField.onChange}
            disablePast={disablePast}
            format={format}
            views={views}
            slotProps={{
              textField: {
                fullWidth: true,
                placeholder: placeholder,
                error: !!fieldState?.error,
                helperText: fieldState?.error?.message,
              },
            }}
          />
        )
      }}
    />
  } else if (field.fieldType === 'datetime') {
    const { fieldName, label, views, format, disablePast, config, placeholder, ...fieldProps } = field

    fieldComponent = (
      <Controller
        {...register(fieldName, config)}
        control={control}
        render={({ field: controlField, fieldState }) => {
          return (
            <DateTimePicker
              {...controlField}
              {...fieldProps}
              label={label}
              onAccept={controlField.onChange}
              disablePast={disablePast}
              format={format || 'DD/MM/YYYY HH:mm'}
              views={views || ['year', 'month', 'day', 'hours', 'minutes']}
              slotProps={{
                textField: {
                  fullWidth: true,
                  placeholder: placeholder,
                  error: !!fieldState?.error,
                  helperText: fieldState?.error?.message,
                },
              }}
            />
          )
        }}
      />
    )
  } else if (field.fieldType === 'switch') {
    const { fieldName, fieldType, config, label, helperText, ...fieldProps } = field

    fieldComponent = (
      <>
        <FormControlLabel
          label={label}
          control={
            <Controller
              {...register(fieldName, config)}
              control={control}
              render={({ field: controlField }) => (
                <Switch
                  {...controlField}
                  {...fieldProps}
                  checked={Boolean(controlField.value)}
                />
              )}
            />
          }
        />
        <FormHelperText>{helperText}</FormHelperText>
      </>
    )
  } else if (field.fieldType === 'checkbox') {
    const { fieldName, fieldType, config, label, helperText, labelPlacement, addTopLabelMargin, ...fieldProps } = field

    fieldComponent = (
      <>
        <FormControlLabel
          label={label}
          sx={{
            mt: addTopLabelMargin ? 3 : 0,
          }}
          labelPlacement={labelPlacement || 'end'}
          control={
            <Controller
              {...register(fieldName, config)}
              control={control}
              render={({ field: controlField }) => {
                const { ref: _ref, ...rest } = controlField

                return (
                  <Checkbox
                    {...rest}
                    {...fieldProps}
                    checked={controlField.value}
                  />
                )
              }}
            />
          }
        />
        <FormHelperText>{helperText}</FormHelperText>
      </>
    )
  } else if (field.fieldType === 'radiogroup') {
    const { fieldName, fieldType, config, label, options, ...fieldProps } = field

    fieldComponent = (
      <Controller
        {...register(fieldName, config)}
        control={control}
        render={({ field: controlField }) => {
          const { ref: _ref, ...rest } = controlField

          return (
            <RadioGroup
              {...rest}
              {...fieldProps}
              label={label}
              options={options}
            />
          )
        }}
      />
    )
  } else if (field.fieldType === 'address') {
    const { fieldName, fieldType, label, ...fieldProps } = field

    fieldComponent = (
      <Controller
        {...register(fieldName)}
        control={control}
        rules={{
          validate: (value: AddressValue) => {
            if (
              value &&
              value.street?.trim() &&
              value.suburb?.trim() &&
              value.state?.trim() &&
              value.postcode?.trim() &&
              value.country?.trim()
            ) {
              return true
            }

            return 'Please fill in all required address fields'
          },
        }}
        render={({ field: controlField, fieldState }) => {
          const { ref, ...rest } = controlField

          return (
            <AddressFields
              {...rest}
              error={Boolean(fieldState.error)}
              {...fieldProps}
            />
          )
        }}
      />
    )
  } else if (field.fieldType === 'map') {
    const { fieldName, fieldType, label, config, ...fieldProps } = field

    fieldComponent = (
      <Controller
        {...register(fieldName, config)}
        control={control}
        render={({ field: controlField, fieldState }) => (
          <MapField
            label={label}
            value={controlField.value}
            onChange={controlField.onChange}
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
            {...fieldProps}
          />
        )}
      />
    )
  } else if (field.fieldType === 'orderItems') {
    const { fieldName, fieldType, label, ...fieldProps } = field

    fieldComponent = (
      <Controller
        {...register(fieldName)}
        control={control}
        rules={{
          validate: (value: OrderItemUpdate[]) => {
            if (value.length === 0) {
              return true
            }

            // iterate through each of the order item update, check if the description is valid
            for (const item of value) {
              if (!item.description) {
                return 'Please fill in all required order item fields'
              }
            }

            return true
          },
        }}
        render={({ field: controlField, fieldState }) => {
          const { ref, ...rest } = controlField

          return (
            <OrderItemsField
              {...rest}
              error={Boolean(fieldState.error)}
              {...fieldProps}
            />
          )
        }}
      />
    )
  } else if (field.fieldType === 'section') {
    const { label, showDivider } = field

    fieldComponent = (
      <>
        {showDivider && <Divider
          sx={{
            mt: 2,
            mb: 1,
          }}
        />}
        <Typography
          variant="body1"
          sx={{
            fontSize: pxToRem(16),
            fontWeight: 500,
            mt: !showDivider ? 1 : 0,
            mb: 1,
          }}
        >
          {label}
        </Typography>
      </>
    )
  } else if (field.fieldType === 'file') {
    const { 
      fieldType,
      fieldName, 
      label, 
      accept, 
      maxSize,
      showFileSize,
      allowClear,
      config,
      helperText,
      ...fieldProps 
    } = field
    
    fieldComponent = (
      <Controller
        {...register(fieldName, config)}
        control={control}
        render={({ field: controlField, fieldState }) => {
          const handleFileChange = (file: File | null) => {
            controlField.onChange(file)
          }
  
          const handleError = (error: string) => {
            // You might want to show this error in your form
            setError(fieldName, { message: error })
          }
  
          return (
            <FileInputField
              {...fieldProps}
              value={controlField.value}
              onChange={handleFileChange}
              onError={handleError}
              accept={accept}
              maxSize={maxSize}
              showFileSize={showFileSize}
              allowClear={allowClear}
              label={label}
              error={fieldState.error?.message}
              disabled={controlField.disabled}
            />
          )
        }}
      />
    )
  } else if (field.fieldType === 'openingHours') {
    const { fieldName, config, ...fieldProp } = field

    fieldComponent = <Controller
      {...register(fieldName, config)}
      control={control}
      render={({ field: controlField, fieldState }) => {
        const { ref, ...rest } = controlField

        return (
          <OpeningHoursField
            {...rest}
            onChange={(value) => {
              controlField.onChange(value)
              controlField.onBlur()
            }}
            disabled={fieldProp.disabled}
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
          />
        )
      }}
    />
  }

  return (
    <Grid
      size={12}
      sx={{
        visibility: isFieldVisible ? 'visible' : 'hidden',
        height: isFieldVisible ? 'auto' : 0,
      }}
      {...gridProps}
    >
      {fieldComponent}
    </Grid>
  )
}